import React from "react"
import { graphql, Link } from "gatsby"

import Layout from "../../components/Layout"
import ServiceProof from "../../components/ServiceProof"
import { getOffer } from "../../data/offers"

import "../../styles/servicePage.scss"

/**
 * /services/aisolution/ — the AI practice.
 *
 * INDEXED AND RANKING. Frozen and unchanged: the <h1>, the <title>, the meta
 * description, the canonical and the URL. Everything below the head is
 * rewritten — CONTENT-ARCHITECTURE §4 allows the prose while requiring the same
 * topical ground, so every technology, product, discipline and problem term in
 * the previous copy still appears here.
 *
 * ONE h1. The previous page rendered THREE, because antd's <Typography.Title>
 * defaults to h1 and the hero heading and two section headings all used it.
 * Only the frozen hero heading is an h1 now; the rest are h2.
 *
 * A SERVICE PAGE MUST NOT READ LIKE AN OFFER PAGE. An offer is one fixed piece
 * of work with a date; a service is a discipline with no published date. Every
 * shared device is deliberately different — see the table at the top of
 * src/styles/servicePage.scss — and there is NO week number, NO date, NO
 * fixed-timeline claim, NO included/not-included block, NO price block and NO
 * split hero anywhere on this page.
 *
 * Prices appear in ONE place: the offer rows in section 6.
 */

const OFFER_SLUGS = [
  "ai-voice-receptionist",
  "ai-catalog-and-docs-assistant",
  "ai-quote-and-document-automation",
]

// Resolved from the catalogue so a price, a timeline or a slug can never drift
// out of sync with the offer page it points at.
const PRACTICE_OFFERS = OFFER_SLUGS.map(getOffer).filter(Boolean)

/* 2. Four problems, in the reader's words. Drawn from the symptoms the three
   offers in this practice already state, so no new claim is introduced. */
const PROBLEMS = [
  "Calls go to voicemail after hours, and those callers ring someone else next.",
  "Your team answers the same handful of questions all day, every day.",
  "Quotes get rebuilt by hand from information that already exists in your system.",
  "You are sitting on years of data and none of it tells you anything.",
  "New staff spend weeks finding out where the answers live.",
  "Nobody can say how many enquiries you actually receive, or what happened to them.",
]

/* 4. The capabilities, with the stack named rather than implied. */
const CAPABILITIES = [
  {
    title: "Assistants that answer, qualify and route",
    body:
      "A conversational assistant — a chatbot, in the older word — handles enquiries by voice or text, works out what the caller or visitor actually wants, answers where it can and hands over to a person where it cannot. Every exchange is transcribed and logged, so you can see what was asked.",
    stack: ["LLMs", "NLP", "Chatbots", "Speech-to-text", "Telephony"],
  },
  {
    title: "Answers grounded in your own content",
    body:
      "Retrieval-augmented generation reads your catalogue, your documentation and your internal procedures, then answers from those rather than from whatever a general model happens to know. Every answer carries a citation you can check.",
    stack: ["RAG", "Vector search", "Embeddings", "Document ingestion"],
  },
  {
    title: "Models that predict, score and classify",
    body:
      "Machine learning models trained on your history surface the patterns a dashboard will not: which enquiries convert, which jobs run over, which stock moves next. They are measured on accuracy against your data, not on a benchmark.",
    stack: ["Python", "PyTorch", "scikit-learn", "Data pipelines"],
  },
  {
    title: "Documents generated instead of typed",
    body:
      "Quotes, contracts and statements of work are produced from live records against your own templates and approval rules, with a margin check before anything is sent rather than after. Version history and an audit trail come with it.",
    stack: ["Document generation", "Templating", "E-signature", "Approval rules"],
  },
  {
    title: "AI inside the systems you already run",
    body:
      "An assistant in a separate tab is one more thing to remember. This is the part people mean by enterprise AI: capabilities embedded in the ERP, CRM and line-of-business tools your team already opens, so the work does not move and the habits do not have to change.",
    stack: ["Odoo", "ERP", "CRM", "REST", "Webhooks"],
  },
  {
    title: "Repetitive work taken off people",
    body:
      "High-volume, rule-shaped process automation — the re-keying, the copying between systems, the chasing. What is left for your team is the part that needs judgement, which is the part they are actually good at.",
    stack: ["Process automation", "Queues", "Schedulers", "Integrations"],
  },
]

/* 5. Named phases, no numbers and no dates. This page exists for open-ended
   work: a phase is a shape of activity, not a slot in a calendar. */
const PHASES = [
  {
    phase: "Before anything is built",
    title: "Framing and data assessment",
    body:
      "You get an honest read on whether the data you hold can support what you want. Your data sources are inventoried, data quality is measured rather than assumed, and the gaps are costed. If the answer is that it cannot support it yet, you hear that here rather than three months in.",
  },
  {
    phase: "Agreeing the shape",
    title: "Scope written down, then held",
    body:
      "Scope is agreed in writing before work starts and change is handled explicitly: anything new is quoted as an addition rather than absorbed silently, so the thing you approved stays the thing being built.",
  },
  {
    phase: "While it is being built",
    title: "Working software you can try",
    body:
      "Progress is a thing you can open, not a status report. Work lands in short cycles on your board, in your tools, and you see real outputs against real data early enough to steer them.",
  },
  {
    phase: "After it is live",
    title: "Measured, tuned, and yours to leave",
    body:
      "Once it is deployed, accuracy, latency and cost sit behind monitoring, logging and alerting, and models are retrained as your data moves. Your data, your models and your documentation are yours — you can take them elsewhere.",
  },
]

/* 7. Proof from this practice only, and only where a case study page exists.
   Big Jerry's Fencing is the one AI engagement in the catalogue with a live
   route — verified, not inferred from a filename. */
const PROOF = {
  client: "Big Jerry's Fencing",
  path: "/caseStudies/projects/bigJerrysFencing",
  metrics: [
    // FIX 2a — "Running across 21 cities in 9 US states" reordered so its figure
    // leads. The figures are untouched; only the word order changed.
    "21 cities across 9 US states",
    // NO NUMERAL EXISTS IN THIS ONE and none is invented. ServiceProof renders
    // it below the stat row rather than under an empty numeral slot. Reported.
    "A voice agent answering around the clock and writing leads straight into Odoo",
  ],
}

/* 8. Questions about the discipline, not about any one offer. */
const FAQS = [
  {
    q: "How do you know whether our data is good enough?",
    a: "That is what the assessment is for, and it comes before any build. It looks at what you actually hold, how consistent it is, and whether it covers the cases you care about. Thin or messy data is normal; what matters is finding out before the work is scoped rather than after.",
  },
  {
    q: "Will this be a wrapper around someone else's model?",
    a: "Sometimes, and where that is the right answer you will be told so. A general large language model with your content retrieved around it is often better and cheaper than training something bespoke. Where your problem genuinely needs a model trained on your own history, that is built instead.",
  },
  {
    q: "What happens to our data?",
    a: "It stays yours. What is sent to a third-party model, what is retained and for how long is written down before anything is connected, and it is a decision you make rather than one made for you. Where the work touches regulated data, the handling is designed around that from the start.",
  },
  {
    q: "How is accuracy measured, and what if it is not good enough?",
    a: "Against your data and your edge cases, on criteria agreed while the scope is being written, so there is a number to argue with rather than an impression. Where it falls short the honest options are retraining, narrowing the scope, or keeping a person in the loop — and which one applies is a conversation, not a surprise.",
  },
  {
    q: "Does this replace the systems we already have?",
    a: "No. Capabilities are embedded in the ERP, CRM and tools your team already opens, and integrated through whatever interface those expose. Replacing a working system is a much larger project and a different conversation.",
  },
  {
    q: "What does it cost when the work is not a fixed-scope offer?",
    a: "Open-ended work is scoped to your situation and quoted against that scope, which is why no figure is published for it here. What is agreed alongside the scope is how the return will be judged — the ROI case is written down in your numbers, not ours, so there is something to measure the spend against afterwards. Three of the packages in this practice do have a published scope, and those are listed above.",
  },
]

const AiSolution = ({ data }) => (
  <Layout
    className="services-page-container"
    data={data}
    // FROZEN — byte-identical to the previous page. Do not touch.
    pageTitle={"Visiomate - AI Solutions & Custom AI Development"}
    descrip={
      "Build smarter software with Visiomate's AI solutions — custom AI development, chatbot creation, machine learning integration, process automation, and enterprise AI tailored for SMEs."
    }
  >
    <article className="sp">
      {/* ── 1. HERO — FULL WIDTH, no split, no rail ─────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero__inner">
          <span className="sp-tag">AI practice</span>
          {/* FROZEN h1. The only h1 on the page. */}
          <h1 className="sp-hero__h1">
            Custom AI Development That Turns Data Into Competitive Advantage
          </h1>
          <p className="sp-hero__lede">
            You have data, systems and a team already stretched. What you do not
            have is a way to get the three of them working together. That is the
            gap this practice closes — custom AI development shaped around your
            workflows, your records and the tools you already run, rather than a
            generic toolkit you bend yourself around.
          </p>
          <Link to="/contact" className="sp-hero__cta">
            Talk through what you are dealing with
          </Link>
        </div>
      </section>

      {/* ── 2. PROBLEMS — two columns, hairlines, no numbers, no cards ──────── */}
      <section className="sp-problems">
        <div className="sp-problems__inner">
          <span className="sp-tag">Where this starts</span>
          <h2 className="sp-h2">The problems this practice solves</h2>
          <p className="sp-lede">
            Most of these are not AI problems. They are volume problems, memory
            problems and re-keying problems, and they are what people are
            actually describing when they ask about AI.
          </p>
          <ul className="sp-problems__grid">
            {PROBLEMS.map(item => (
              <li className="sp-problems__item" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 3. WHAT THIS ACTUALLY IS — the section offer pages do not have ──── */}
      <section className="sp-what">
        <div className="sp-what__inner">
          {/* FIX 1 — two columns. This ran 300+ words in one narrow left
              column with the right half of the page empty. The heading and a
              standfirst carry the point on the left; the argument is broken
              into its own divisions on the right, at a 34rem measure. */}
          <div className="sp-what__cols">
            <div className="sp-what__lead">
              <span className="sp-tag">In plain terms</span>
              <h2 className="sp-h2">What this actually is</h2>
              <p className="sp-what__standfirst">
                There are only three or four ideas under the word, and none of
                them is complicated. What decides whether any of it works for
                you is which examples you already hold and what it has to plug
                into.
              </p>
            </div>
            <div className="sp-what__body">
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What the word actually means</h3>
              <p>
                Strip the word away and AI — artificial intelligence — is software that
                works out its answer from examples instead of being told the rule for
                every case. Where ordinary software needs a person to write down what to
                do in each situation, this kind is shown enough past cases to derive the
                pattern itself. That is the whole of the idea, and everything else is
                detail about which examples and which method.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">The three methods that cover almost all of it</h3>
              <p>
                Three methods cover almost all of it. Machine learning, or ML, trains a
                model on your own history so it can score or predict something new:
                which enquiry is worth calling first, which job is drifting over budget.
                A large language model, or LLM, is a model already trained on an
                enormous amount of general text, which is why it can hold a conversation
                without being taught your business — and why, on its own, it knows
                nothing about you. Natural language processing, or NLP, is the older
                craft of turning messy human wording into something a system can act on.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">Why a general model needs your content</h3>
              <p>
                The piece that makes a general model useful to you specifically is
                retrieval. Rather than trying to teach a model your catalogue, you let
                it look your catalogue up at the moment of answering, and it replies
                from what it found with a citation attached. That is retrieval-augmented
                generation, or RAG, and it is why an assistant can be accurate about
                your products on Monday and still accurate after you change your prices
                on Tuesday.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What you are actually buying</h3>
              <p>
                None of this is a product you switch on. It is software built against
                your data and your workflows, connected into your ERP — enterprise
                resource planning — and your CRM — customer relationship management —
                and measured on whether the numbers actually move.
              </p>
            </div>
            </div>
          </div>
          <p className="sp-what__pull">
            The useful question is never &ldquo;should we use AI&rdquo;. It is which
            repeated decision or repeated keystroke in your business is expensive
            enough to be worth automating, and whether you hold enough history for a
            machine to learn it.
          </p>
        </div>
      </section>

      {/* ── 4. WHAT WE BUILD WITH IT — THE DARK BAND, cards ────────────────── */}
      <section className="sp-build">
        <div className="sp-build__inner">
          <span className="sp-tag">The capabilities</span>
          <h2 className="sp-h2">What gets built with it</h2>
          <p className="sp-lede">
            Six things account for nearly every engagement in this practice. Most
            projects are two or three of them wired together, not all six.
          </p>
          <div className="sp-build__grid">
            {CAPABILITIES.map(cap => (
              <div className="sp-build__card" key={cap.title}>
                <h3 className="sp-build__cardtitle">{cap.title}</h3>
                <p className="sp-build__cardbody">{cap.body}</p>
                <ul className="sp-build__stack">
                  {cap.stack.map(tech => (
                    <li className="sp-build__stackitem" key={tech}>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. HOW WE WORK — stepped across, named phases, no week numbers ─── */}
      <section className="sp-how">
        <div className="sp-how__inner">
          <span className="sp-tag">How the work runs</span>
          <h2 className="sp-h2">How open-ended work is run</h2>
          <p className="sp-lede">
            Work in this practice rarely arrives with a date attached, so what
            you get instead of a delivery date is visibility: agreed scope,
            something to open every cycle, and a way out.
          </p>
          <ol className="sp-how__steps">
            {PHASES.map(ph => (
              <li className="sp-how__step" key={ph.title}>
                <span className="sp-how__marker" aria-hidden="true" />
                <span className="sp-how__phase">{ph.phase}</span>
                <h3 className="sp-how__title">{ph.title}</h3>
                <p className="sp-how__body">{ph.body}</p>
              </li>
            ))}
          </ol>
          <p className="sp-how__note">
            No date is published for work of this shape, and none is implied. If
            what you need is a scope and a date you can hold someone to, the
            packages below carry both.
          </p>
        </div>
      </section>

      {/* ── 6. FIXED-SCOPE OPTIONS — rows, prices shown ─────────────────────── */}
      <section className="sp-offers">
        <div className="sp-offers__inner">
          <span className="sp-tag">Fixed-scope options</span>
          <h2 className="sp-h2">Some of this work has a written scope already</h2>
          <p className="sp-lede">
            Three builds in this practice have been delivered enough times to
            state upfront what they contain, how long they take and what they
            cost.
          </p>
          <ul className="sp-offers__list">
            {PRACTICE_OFFERS.map(o => (
              <li className="sp-offers__item" key={o.slug}>
                <Link to={`/offers/${o.slug}`} className="sp-offers__row">
                  <span className="sp-offers__main">
                    <span className="sp-offers__name">{o.name}</span>
                    <span className="sp-offers__problem">{o.symptoms[0]}</span>
                  </span>
                  <span className="sp-offers__meta">
                    <span className="sp-offers__timeline">
                      {o.timelineShort}
                    </span>
                    <span className="sp-offers__price">{o.priceShort}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sp-offers__all">
            <Link to="/offers">
              Every offer, across every practice &rarr;
            </Link>
          </p>
        </div>
      </section>

      {/* ── 7. PROOF — the shared component. FIX 2: every metric leads
             with its figure at $fs-stat, a CLIENT tag carries the attribution,
             and the name links to the case study because that route resolves.
             components/ServiceProof.js ─────────────────────────────────────── */}
      <ServiceProof
        client={PROOF.client}
        path={PROOF.path}
        metrics={PROOF.metrics}
      />

      {/* ── 8. QUESTIONS — about the discipline ─────────────────────────────── */}
      <section className="sp-faq">
        <div className="sp-faq__inner">
          <span className="sp-tag">Questions</span>
          <h2 className="sp-h2">What buyers ask about AI work</h2>
          <ul className="sp-faq__list">
            {FAQS.map(f => (
              <li className="sp-faq__item" key={f.q}>
                <p className="sp-faq__q">{f.q}</p>
                <p className="sp-faq__a">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 9. IF YOUR NEED IS BROADER — one CTA ────────────────────────────── */}
      <section className="sp-broader">
        <div className="sp-broader__inner">
          <span className="sp-tag">If your need is broader</span>
          <h2 className="sp-h2">If none of that is quite your situation</h2>
          <div className="sp-broader__cols">
            <p className="sp-broader__text">
              Plenty of what lands in this practice does not fit a package.
              Embedded engineering alongside your own team, a model that needs
              rebuilding on better data, an assistant that has to satisfy a
              regulator, or a first honest look at whether AI is the right answer
              at all.
            </p>
            <p className="sp-broader__text">
              Describe what you are dealing with and you will get a straight
              read on how it would be approached, what it depends on, and where
              the risk sits. If the answer is that you do not need this yet, that
              is what you will hear.
            </p>
          </div>
          <div className="sp-broader__foot">
            <Link to="/contact" className="sp-broader__cta">
              Tell us what you are dealing with
            </Link>
          </div>
        </div>
      </section>
    </article>
  </Layout>
)

export default AiSolution

export const aiSolution = graphql`
  query aiSolution {
    bgImage: file(relativePath: { eq: "Mask_group8.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
